import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useParams, useOutletContext } from "react-router-dom";
import { api, API_BASE } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { apiErrorMessage } from "../../lib/errors";
import { SkeletonBlock } from "../../components/skeleton";
import { MediaPicker } from "../../components/media-picker";
import type {
  Plan,
  Platform,
  ProjectStatus,
  VariantLength,
  VariantTone,
  VariantModel,
} from "@brandecho/shared";
import { isModelAllowed, MODEL_MIN_PLAN } from "@brandecho/shared";
import type { MeResponse } from "../../lib/auth";

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatLocalDatetime(ms: number): string {
  const d = new Date(ms);
  d.setSeconds(0);
  d.setMilliseconds(0);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatHourShort(h: number): string {
  if (h === 0) return "12a";
  if (h === 12) return "12p";
  return h < 12 ? `${h}a` : `${h - 12}p`;
}

function nextOccurrenceMs(targetDay: number, targetHour: number): number {
  const now = new Date();
  const d = new Date(now);
  d.setHours(targetHour, 0, 0, 0);
  let diffDays = (targetDay - d.getDay() + 7) % 7;
  if (diffDays === 0 && d.getTime() <= now.getTime()) diffDays = 7;
  d.setDate(d.getDate() + diffDays);
  return d.getTime();
}

interface OutletCtx {
  me: MeResponse;
  refreshMe: () => Promise<void>;
}

interface ProjectRow {
  id: string;
  user_id: string;
  voice_profile_id: string | null;
  title: string | null;
  input_text: string;
  mood: string | null;
  status: ProjectStatus;
  created_at: number;
  updated_at: number;
}

interface VariantRow {
  id: string;
  platform: Platform;
  content: string;
  content_edited: string | null;
  edit_count: number;
  generation_meta: string | null;
  version_index: number;
  is_active: number;
  created_at: number;
  updated_at: number;
}

const PLATFORM_LABEL: Record<Platform, string> = {
  twitter: "X / Twitter",
  bluesky: "Bluesky",
  linkedin: "LinkedIn",
  newsletter: "Newsletter",
  short_video: "Short video",
};

const PLATFORM_ORDER: Platform[] = [
  "twitter",
  "bluesky",
  "linkedin",
  "newsletter",
  "short_video",
];

const SCRIPT_ONLY_PLATFORMS = new Set<Platform>(["newsletter", "short_video"]);
function isScriptOnly(p: Platform): boolean {
  return SCRIPT_ONLY_PLATFORMS.has(p);
}

function downloadAsText(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ProjectDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { me } = useOutletContext<OutletCtx>();
  const userPlan = (me.subscription.plan ?? "free") as Plan;
  const [project, setProject] = useState<ProjectRow | null>(null);
  const [variants, setVariants] = useState<VariantRow[]>([]);
  const [tab, setTab] = useState<Platform>("twitter");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<number | null>(null);

  async function load() {
    try {
      const res = await api.get<{ project: ProjectRow; variants: VariantRow[] }>(
        `/api/projects/${id}`
      );
      setProject(res.project);
      setVariants(res.variants);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    return () => {
      if (pollingRef.current !== null) window.clearInterval(pollingRef.current);
    };
  }, [id]);

  useEffect(() => {
    if (!project) return;
    const inProgress = project.status === "queued" || project.status === "generating";
    if (inProgress) {
      pollingRef.current = window.setInterval(load, 2500);
    } else if (pollingRef.current !== null) {
      window.clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, [project?.status]);

  if (loading) {
    return (
      <div>
        <div className="mb-4 h-3 w-32 animate-pulse rounded bg-ink-200/70" />
        <div className="mb-2 h-7 w-2/3 animate-pulse rounded bg-ink-200/70" />
        <div className="mb-6 h-3 w-1/3 animate-pulse rounded bg-ink-200/70" />
        <div className="card">
          <SkeletonBlock lines={6} />
        </div>
      </div>
    );
  }
  if (error)
    return (
      <div className="card">
        <p className="text-red-600">{error}</p>
        <Link to="/app" className="btn-secondary mt-4 inline-flex no-underline">
          Back
        </Link>
      </div>
    );
  if (!project) return null;

  const variantsByPlatform = new Map<Platform, VariantRow[]>();
  for (const v of variants) {
    const arr = variantsByPlatform.get(v.platform) ?? [];
    arr.push(v);
    variantsByPlatform.set(v.platform, arr);
  }
  const platformVersions = variantsByPlatform.get(tab) ?? [];
  const activeVariant = platformVersions.find((v) => v.is_active === 1) ?? platformVersions[0];
  const totalReady = Array.from(variantsByPlatform.values()).filter((arr) =>
    arr.some((v) => v.is_active === 1)
  ).length;

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link
            to="/app"
            className="text-sm text-ink-500 no-underline hover:text-ink-900"
          >
            ← Projects
          </Link>
          <h1 className="page-title mt-1">
            {project.title ?? "Untitled"}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-500">
            <span>{new Date(project.created_at).toLocaleString()}</span>
            <span>·</span>
            <span>status:</span>
            <StatusBadge status={project.status} />
            {project.mood ? (
              <>
                <span>·</span>
                <span className="rounded-full bg-ink-100 px-2 py-0.5 text-ink-700">
                  mood: {project.mood}
                </span>
              </>
            ) : null}

          </p>
        </div>
        {project.status === "ready" ? (
          <div className="flex gap-2">
            <a
              href={`/api/projects/${project.id}/export?format=md`}
              className="btn-secondary no-underline"
            >
              Export .md
            </a>
            <a
              href={`/api/projects/${project.id}/export?format=txt`}
              className="btn-secondary no-underline"
            >
              Export .txt
            </a>
          </div>
        ) : null}
      </div>

      {project.status !== "ready" && project.status !== "failed" ? (
        <div className="card mb-6">
          <p className="text-ink-700">
            Generating variants… ({totalReady}/4 ready)
          </p>
          <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-ink-200">
            <div
              className="h-1 bg-accent transition-all"
              style={{ width: `${(totalReady / 4) * 100}%` }}
            />
          </div>
        </div>
      ) : null}

      {project.status === "failed" ? (
        <div className="card mb-6 border-red-300 bg-red-50">
          <p className="text-red-700">
            Generation failed. Your credit was not refunded automatically — contact
            support if this persists.
          </p>
        </div>
      ) : null}

      <div className="card">
        <div className="mb-4 flex flex-wrap gap-1 border-b border-ink-200">
          {PLATFORM_ORDER.map((p) => {
            const ready = (variantsByPlatform.get(p) ?? []).some(
              (v) => v.is_active === 1
            );
            const active = tab === p;
            return (
              <button
                key={p}
                onClick={() => setTab(p)}
                className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "border-ink-900 text-ink-900"
                    : "border-transparent text-ink-500 hover:text-ink-900"
                }`}
              >
                {PLATFORM_LABEL[p]}
                {ready ? (
                  <span
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-bold text-emerald-700"
                    title="Ready"
                  >
                    ✓
                  </span>
                ) : (
                  <span
                    className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500"
                    title="Generating"
                  />
                )}
              </button>
            );
          })}
        </div>

        {activeVariant ? (
          <VariantEditor
            key={activeVariant.id}
            projectId={project.id}
            variant={activeVariant}
            versions={platformVersions}
            userPlan={userPlan}
            onSaved={load}
          />
        ) : project.status === "queued" || project.status === "generating" ? (
          <div className="py-12 text-center text-ink-500">
            <p>Generating {PLATFORM_LABEL[tab]}…</p>
          </div>
        ) : (
          <MissingVariantPanel
            projectId={project.id}
            platform={tab}
            label={PLATFORM_LABEL[tab]}
            onQueued={load}
          />
        )}
      </div>
    </div>
  );
}

function MissingVariantPanel(props: {
  projectId: string;
  platform: Platform;
  label: string;
  onQueued: () => void;
}) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  async function generate() {
    setSubmitting(true);
    try {
      await api.post(
        `/api/projects/${props.projectId}/variants/${props.platform}/generate-missing`,
        {}
      );
      toast.success(`${props.label} variant queued — refresh in a few seconds.`);
      props.onQueued();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <div className="py-12 text-center">
      <p className="mb-4 text-sm text-ink-500">
        No {props.label} variant for this project yet.
      </p>
      <button
        onClick={generate}
        disabled={submitting}
        className="btn-primary text-sm"
      >
        {submitting ? "Queuing…" : `Generate ${props.label} variant`}
      </button>
    </div>
  );
}

function StatusBadge({ status }: { status: ProjectStatus }) {
  const colors: Record<ProjectStatus, string> = {
    queued: "bg-ink-100 text-ink-700",
    generating: "bg-blue-100 text-blue-700",
    ready: "bg-green-100 text-green-700",
    failed: "bg-red-100 text-red-700",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs ${colors[status]}`}>
      {status}
    </span>
  );
}

function VariantEditor({
  projectId,
  variant,
  versions,
  userPlan,
  onSaved,
}: {
  projectId: string;
  variant: VariantRow;
  versions: VariantRow[];
  userPlan: Plan;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [text, setText] = useState(variant.content_edited ?? variant.content);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenLength, setRegenLength] = useState<VariantLength | "">("");
  const [regenTone, setRegenTone] = useState<VariantTone | "">("");
  const [regenInstruction, setRegenInstruction] = useState("");
  const [regenMode, setRegenMode] = useState<"append" | "replace">("append");
  const [regenModel, setRegenModel] = useState<VariantModel>("auto");
  const [regenerating, setRegenerating] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [visualOpen, setVisualOpen] = useState(false);





  const dirty = text !== (variant.content_edited ?? variant.content);
  const meta: {
    tokens_in?: number;
    tokens_out?: number;
    cost_usd?: number;
    model?: string;
    length?: string | null;
    tone?: string | null;
    instruction?: string | null;
    is_regen?: boolean;
  } = (() => {
    try {
      return variant.generation_meta ? JSON.parse(variant.generation_meta) : {};
    } catch {
      return {};
    }
  })();

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard.");
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy. Please try again.");
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.patch(`/api/projects/${projectId}/variants/${variant.platform}`, {
        content_edited: text,
      });
      toast.success("Edits saved.");
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setText(variant.content);
  }

  async function handleRegenerate() {
    setRegenerating(true);
    setRegenOpen(false);
    setText("");
    try {
      const url = `/api/projects/${projectId}/variants/${variant.platform}/regenerate-stream`;
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: regenMode,
          length: regenLength || undefined,
          tone: regenTone || undefined,
          instruction: regenInstruction.trim() || undefined,
          model: regenModel,
        }),
      });
      if (!res.ok || !res.body) {
        const txt = await res.text().catch(() => "");
        let msg = "Regenerate failed.";
        let isUpgrade = false;
        try {
          const j = JSON.parse(txt);
          msg = j.message ?? j.error ?? msg;
          if (j.error === "MODEL_REQUIRES_UPGRADE") {
            isUpgrade = true;
            msg = `${j.message} Visit /pricing to upgrade.`;
          }
        } catch {
          /* ignore */
        }
        setRegenerating(false);
        setText(variant.content_edited ?? variant.content);
        toast.error(msg);
        if (isUpgrade) {
          setRegenOpen(true);
        }
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let streamed = "";
      let errored = false;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";
        for (const block of events) {
          const dataLine = block
            .split("\n")
            .find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          const payload = dataLine.slice(5).trim();
          if (!payload) continue;
          try {
            const ev = JSON.parse(payload);
            if (ev.type === "delta" && typeof ev.text === "string") {
              streamed += ev.text;
              setText(streamed);
            } else if (ev.type === "done") {
              toast.success("Regenerated.");
            } else if (ev.type === "error") {
              errored = true;
              toast.error(ev.message ?? "Stream error.");
            }
          } catch {
            /* ignore malformed event */
          }
        }
      }
      setRegenInstruction("");
      onSaved();
      if (errored) {
        setText(variant.content_edited ?? variant.content);
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
      setText(variant.content_edited ?? variant.content);
    } finally {
      setRegenerating(false);
    }
  }

  async function activateVersion(versionId: string) {
    try {
      await api.post(
        `/api/projects/${projectId}/variants/${variant.platform}/versions/${versionId}/activate`,
        {}
      );
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  async function deleteVersion(versionId: string) {
    if (versions.length <= 1) {
      toast.error("Can't delete the only version.");
      return;
    }
    try {
      await api.del(
        `/api/projects/${projectId}/variants/${variant.platform}/versions/${versionId}`
      );
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  return (
    <div>
      {versions.length > 1 ? (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs text-ink-500">Versions:</span>
          {versions.map((v) => {
            const isActive = v.is_active === 1;
            return (
              <div key={v.id} className="flex items-center">
                <button
                  onClick={() => !isActive && activateVersion(v.id)}
                  className={`rounded-l-full border-y border-l px-2.5 py-1 text-xs font-medium transition-colors ${
                    isActive
                      ? "border-ink-900 bg-ink-900 text-white"
                      : "border-ink-200 bg-white text-ink-700 hover:border-ink-400"
                  }`}
                >
                  v{v.version_index}
                </button>
                <button
                  onClick={() => deleteVersion(v.id)}
                  disabled={versions.length <= 1}
                  className={`rounded-r-full border-y border-r px-1.5 py-1 text-xs transition-colors disabled:opacity-30 ${
                    isActive
                      ? "border-ink-900 bg-ink-900 text-white hover:bg-red-700 hover:border-red-700"
                      : "border-ink-200 bg-white text-ink-500 hover:border-red-400 hover:text-red-600"
                  }`}
                  title="Delete this version"
                >
                  ×
                </button>
              </div>
            );
          })}
          <span className="ml-2 text-xs text-ink-400">
            {versions.length}/5
          </span>
        </div>
      ) : null}

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-ink-500">
        <span>
          {text.length.toLocaleString()} chars
          {meta.model || meta.length || meta.tone || meta.is_regen ? (
            <span className="ml-3 inline-flex flex-wrap gap-1">
              {meta.model ? (
                <span className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-700">
                  {meta.model}
                </span>
              ) : null}
              {meta.length ? (
                <span className="rounded bg-ink-100 px-1.5 py-0.5">
                  length: {meta.length}
                </span>
              ) : null}
              {meta.tone ? (
                <span className="rounded bg-ink-100 px-1.5 py-0.5">
                  tone: {meta.tone}
                </span>
              ) : null}
              {meta.is_regen ? (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-700">
                  regenerated
                </span>
              ) : null}
            </span>
          ) : null}
        </span>
        {variant.edit_count > 0 ? (
          <span>edited {variant.edit_count}×</span>
        ) : null}
      </div>
      <textarea
        className="input min-h-[320px] whitespace-pre-wrap font-mono text-sm leading-relaxed sm:min-h-[400px]"
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={regenerating}
      />
      {regenerating ? (
        <div className="mt-2 flex items-center gap-2 text-sm text-ink-500">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-blue-500" />
          <span>Regenerating with new settings…</span>
        </div>
      ) : null}
      {isScriptOnly(variant.platform) ? (
        <div className="mt-3 rounded-lg border border-amber-200/70 bg-amber-50/60 px-3.5 py-2.5 text-xs text-amber-900">
          <strong className="font-semibold">Script only.</strong>{" "}
          {variant.platform === "newsletter"
            ? "BrandEcho doesn't send email yet — copy or download this and paste into your newsletter tool (Beehiiv, Substack, ConvertKit)."
            : "BrandEcho doesn't post video yet — copy or download this script for your TikTok / YouTube Shorts / Reels recording."}
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={handleCopy} className="btn-secondary" disabled={regenerating}>
          {copied ? "Copied!" : "Copy"}
        </button>
        <button
          onClick={handleSave}
          className="btn-primary"
          disabled={!dirty || saving || regenerating}
        >
          {saving ? "Saving…" : dirty ? "Save edits" : "Saved"}
        </button>
        {variant.content_edited ? (
          <button onClick={handleReset} className="btn-secondary" disabled={regenerating}>
            Reset to original
          </button>
        ) : null}
        {isScriptOnly(variant.platform) ? (
          <button
            type="button"
            onClick={() =>
              downloadAsText(
                `${variant.platform}-${variant.id.slice(-6)}.txt`,
                text
              )
            }
            className="btn-secondary"
            disabled={regenerating}
          >
            Download .txt
          </button>
        ) : (
          <button
            onClick={() => setScheduleOpen(true)}
            className="btn-secondary"
            disabled={regenerating}
          >
            Schedule…
          </button>
        )}
        <button
          onClick={() => setVisualOpen(true)}
          className="btn-secondary"
          disabled={regenerating}
        >
          ✨ Visual
        </button>
        <button
          onClick={() => setRegenOpen((v) => !v)}
          className="btn-secondary ml-auto"
          disabled={regenerating}
        >
          {regenOpen ? "Cancel" : "↻ Regenerate"}
        </button>
      </div>

      {visualOpen ? (
        <VisualModal
          variant={variant}
          text={text}
          onClose={() => setVisualOpen(false)}
        />
      ) : null}

      {scheduleOpen ? (
        <ScheduleModal
          projectId={projectId}
          variant={variant}
          text={text}
          onClose={() => setScheduleOpen(false)}
          onScheduled={() => {
            setScheduleOpen(false);
            toast.success("Scheduled. It'll publish at the chosen time.");
          }}
        />
      ) : null}

      {regenOpen ? (
        <div className="mt-4 space-y-3 rounded-xl border border-ink-200 bg-white p-4">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
              Mode
            </label>
            <div className="flex gap-2">
              <PillRadio
                active={regenMode === "append"}
                onClick={() => setRegenMode("append")}
                label={`Add new version (v${
                  (versions.at(-1)?.version_index ?? 0) + 1
                })`}
              />
              <PillRadio
                active={regenMode === "replace"}
                onClick={() => setRegenMode("replace")}
                label="Replace current"
              />
            </div>
            <p className="mt-1 text-xs text-ink-500">
              {regenMode === "append"
                ? `Keeps current versions and appends a new one. ${versions.length}/5 used.`
                : "Overwrites the current active version. Edits are reset."}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
              Length
            </label>
            <div className="flex gap-2">
              {(["short", "medium", "long"] as VariantLength[]).map((v) => (
                <PillRadio
                  key={v}
                  active={regenLength === v}
                  onClick={() => setRegenLength(regenLength === v ? "" : v)}
                  label={v}
                />
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
              Tone
            </label>
            <div className="flex gap-2">
              {(["professional", "neutral", "casual"] as VariantTone[]).map((v) => (
                <PillRadio
                  key={v}
                  active={regenTone === v}
                  onClick={() => setRegenTone(regenTone === v ? "" : v)}
                  label={v}
                />
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
              Model
            </label>
            <select
              value={regenModel}
              onChange={(e) => setRegenModel(e.target.value as VariantModel)}
              className="input text-sm"
            >
              <ModelOption value="auto" plan={userPlan}>
                Auto — pick by voice profile
              </ModelOption>
              <optgroup label="OpenAI · GPT-5 (newest)">
                <ModelOption value="gpt-5" plan={userPlan}>
                  gpt-5 — flagship
                </ModelOption>
                <ModelOption value="gpt-5-mini" plan={userPlan}>
                  gpt-5-mini — balanced
                </ModelOption>
                <ModelOption value="gpt-5-nano" plan={userPlan}>
                  gpt-5-nano — fastest
                </ModelOption>
              </optgroup>
              <optgroup label="OpenAI · GPT-4.1">
                <ModelOption value="gpt-4.1" plan={userPlan}>
                  gpt-4.1
                </ModelOption>
                <ModelOption value="gpt-4.1-mini" plan={userPlan}>
                  gpt-4.1-mini
                </ModelOption>
                <ModelOption value="gpt-4.1-nano" plan={userPlan}>
                  gpt-4.1-nano
                </ModelOption>
              </optgroup>
              <optgroup label="OpenAI · GPT-4o">
                <ModelOption value="gpt-4o" plan={userPlan}>
                  gpt-4o
                </ModelOption>
                <ModelOption value="gpt-4o-mini" plan={userPlan}>
                  gpt-4o-mini
                </ModelOption>
              </optgroup>
              <optgroup label="OpenAI · Reasoning (slower, deeper)">
                <ModelOption value="o3" plan={userPlan}>
                  o3
                </ModelOption>
                <ModelOption value="o3-mini" plan={userPlan}>
                  o3-mini
                </ModelOption>
                <ModelOption value="o4-mini" plan={userPlan}>
                  o4-mini
                </ModelOption>
              </optgroup>
              <optgroup label="Cloudflare Workers AI · Free">
                <ModelOption value="workers-ai-llama-3.3-70b" plan={userPlan}>
                  Llama 3.3 70B
                </ModelOption>
              </optgroup>
            </select>
            <p className="mt-1 text-xs text-ink-500">
              Your plan: <span className="font-medium">{userPlan}</span>.
              Locked models show the minimum plan required —{" "}
              <Link to="/pricing" className="text-brand-700">
                upgrade
              </Link>{" "}
              to unlock more.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
              Custom instruction <span className="lowercase text-ink-400">(optional)</span>
            </label>
            <textarea
              value={regenInstruction}
              onChange={(e) => setRegenInstruction(e.target.value)}
              placeholder='e.g. "make it half the length", "lead with a question", "更俏皮一点"'
              maxLength={500}
              className="input min-h-[60px] text-sm"
            />
            <p className="mt-1 text-xs text-ink-500">
              The instruction overrides the previous draft and is the primary objective.
            </p>
          </div>
          <div className="flex justify-end gap-2 border-t border-ink-200 pt-3">
            <button onClick={() => setRegenOpen(false)} className="btn-secondary">
              Cancel
            </button>
            <button onClick={handleRegenerate} className="btn-primary">
              Regenerate {PLATFORM_LABEL[variant.platform]}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface SocialAccountLite {
  id: string;
  platform: string;
  handle: string;
  display_name: string | null;
  status: string;
}

type VisualTemplate =
  | "square"
  | "story"
  | "tweet_hero"
  | "linkedin_carousel";

const VISUAL_TEMPLATES: {
  key: VisualTemplate;
  label: string;
  ratio: string;
  description: string;
  format: "png" | "pdf";
}[] = [
  { key: "square", label: "Square", ratio: "1:1", description: "IG feed · X image", format: "png" },
  { key: "story", label: "Story", ratio: "9:16", description: "IG/Threads story", format: "png" },
  { key: "tweet_hero", label: "Tweet hero", ratio: "16:9", description: "X embedded image", format: "png" },
  {
    key: "linkedin_carousel",
    label: "LinkedIn carousel",
    ratio: "PDF · 4:5",
    description: "Document post (5-8 slides)",
    format: "pdf",
  },
];

const VISUAL_PALETTES: { name: string; dark: string; light: string }[] = [
  { name: "Sky", dark: "#0f172a", light: "#0ea5e9" },
  { name: "Sunset", dark: "#7c1d6f", light: "#fb923c" },
  { name: "Forest", dark: "#064e3b", light: "#65a30d" },
  { name: "Crimson", dark: "#7f1d1d", light: "#f59e0b" },
  { name: "Plum", dark: "#1e1b4b", light: "#a78bfa" },
  { name: "Mono", dark: "#0a0a0a", light: "#525252" },
];

function VisualModal({
  variant,
  text,
  onClose,
}: {
  variant: VariantRow;
  text: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const [template, setTemplate] = useState<VisualTemplate>("square");
  const [quoteText, setQuoteText] = useState(() => {
    const trimmed = text.trim().replace(/^\d+\/\s*/gm, "");
    return trimmed.length > 220 ? trimmed.slice(0, 217) + "…" : trimmed;
  });
  const [carouselText, setCarouselText] = useState(() => text.trim());
  const [attribution, setAttribution] = useState("");
  const [paletteIdx, setPaletteIdx] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [resultId, setResultId] = useState<string | null>(null);
  const [resultFormat, setResultFormat] = useState<"png" | "pdf">("png");

  const isCarousel = template === "linkedin_carousel";

  async function generate() {
    const inputText = isCarousel ? carouselText : quoteText;
    if (!inputText.trim()) {
      toast.error("Add some text first.");
      return;
    }
    setGenerating(true);
    setResultId(null);
    try {
      const palette = VISUAL_PALETTES[paletteIdx]!;
      const res = await api.post<{ id: string; format: "png" | "pdf" }>(
        `/api/variants/${variant.id}/visuals`,
        {
          template,
          text: inputText,
          attribution: attribution || null,
          palette_dark: palette.dark,
          palette_light: palette.light,
        }
      );
      setResultId(res.id);
      setResultFormat(res.format);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setGenerating(false);
    }
  }

  async function downloadFile() {
    if (!resultId) return;
    try {
      const res = await fetch(`${API_BASE}/api/visuals/${resultId}/file`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `brandecho-${template}-${resultId}.${resultFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  const previewUrl = resultId
    ? `${API_BASE}/api/visuals/${resultId}/file`
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-4 backdrop-blur-sm animate-fade-in sm:items-center">
      <div className="w-full max-w-3xl rounded-2xl border border-ink-200/60 bg-white p-5 shadow-xl animate-scale-in">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-ink-900">Generate visual</h3>
          <button
            onClick={onClose}
            className="text-ink-500 hover:text-ink-900"
            type="button"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr,260px]">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                Template
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {VISUAL_TEMPLATES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => {
                      setTemplate(t.key);
                      setResultId(null);
                    }}
                    className={`rounded-lg border p-3 text-left transition-colors ${
                      template === t.key
                        ? "border-blue-500 bg-blue-50"
                        : "border-ink-200 hover:border-ink-400"
                    }`}
                  >
                    <div className="text-xs font-medium text-ink-900">
                      {t.label}
                    </div>
                    <div className="text-xs text-ink-500">{t.ratio}</div>
                    <div className="mt-1 text-[10px] text-ink-500">
                      {t.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {isCarousel ? (
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                  Slides source ({carouselText.length}/4000) — paragraphs become
                  separate slides (max 8)
                </label>
                <textarea
                  value={carouselText}
                  onChange={(e) => {
                    setCarouselText(e.target.value);
                    setResultId(null);
                  }}
                  className="input min-h-[180px] text-sm"
                  maxLength={4000}
                  placeholder="Each blank-line-separated paragraph becomes a slide. First slide is the cover, last is the CTA."
                />
              </div>
            ) : (
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                  Quote text ({quoteText.length}/500)
                </label>
                <textarea
                  value={quoteText}
                  onChange={(e) => {
                    setQuoteText(e.target.value);
                    setResultId(null);
                  }}
                  className="input min-h-[100px] text-sm"
                  maxLength={500}
                />
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                Attribution (optional)
              </label>
              <input
                value={attribution}
                onChange={(e) => {
                  setAttribution(e.target.value);
                  setResultId(null);
                }}
                placeholder="@yourname"
                className="input text-sm"
                maxLength={80}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                Palette
              </label>
              <div className="flex flex-wrap gap-2">
                {VISUAL_PALETTES.map((p, i) => (
                  <button
                    key={p.name}
                    type="button"
                    onClick={() => {
                      setPaletteIdx(i);
                      setResultId(null);
                    }}
                    className={`flex items-center gap-2 rounded-md border px-2 py-1 text-xs transition-colors ${
                      paletteIdx === i
                        ? "border-blue-500"
                        : "border-ink-200 hover:border-ink-400"
                    }`}
                  >
                    <span
                      className="h-4 w-4 rounded"
                      style={{
                        background: `linear-gradient(135deg, ${p.dark}, ${p.light})`,
                      }}
                    />
                    {p.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                onClick={generate}
                className="btn-primary text-sm"
                disabled={generating}
              >
                {generating
                  ? "Generating… (3-8s)"
                  : resultId
                    ? "Regenerate"
                    : "Generate"}
              </button>
              {resultId ? (
                <button
                  onClick={downloadFile}
                  className="btn-secondary text-sm"
                  type="button"
                >
                  ↓ Download {resultFormat.toUpperCase()}
                </button>
              ) : null}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
              Preview
            </label>
            <div className="overflow-hidden rounded-lg border border-ink-200 bg-ink-50">
              {previewUrl ? (
                resultFormat === "pdf" ? (
                  <div className="flex flex-col items-center gap-2 p-6 text-center">
                    <div className="text-3xl">📄</div>
                    <p className="text-sm font-medium text-ink-900">
                      PDF carousel ready
                    </p>
                    <p className="text-xs text-ink-500">
                      Multi-page PDF for LinkedIn document post
                    </p>
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Preview in new tab →
                    </a>
                  </div>
                ) : (
                  <img
                    src={previewUrl}
                    alt="Visual preview"
                    className="h-auto w-full"
                  />
                )
              ) : (
                <div className="flex aspect-square items-center justify-center px-4 text-center text-xs text-ink-500">
                  Click Generate to render the {template.replace("_", " ")}. The
                  first render in a while may take 5-10 seconds while the
                  browser warms up.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScheduleModal({
  projectId,
  variant,
  text,
  onClose,
  onScheduled,
}: {
  projectId: string;
  variant: VariantRow;
  text: string;
  onClose: () => void;
  onScheduled: () => void;
}) {
  const toast = useToast();
  const [accounts, setAccounts] = useState<SocialAccountLite[]>([]);
  const [accountId, setAccountId] = useState<string>("");
  const [when, setWhen] = useState(() => formatLocalDatetime(Date.now() + 60 * 60 * 1000));
  const [submitting, setSubmitting] = useState(false);
  const [mediaIds, setMediaIds] = useState<string[]>([]);
  const [firstComment, setFirstComment] = useState("");
  const [bestSlots, setBestSlots] = useState<{ day: number; hour: number; avg_score: number; post_count: number }[]>([]);

  useEffect(() => {
    api
      .get<{ social_accounts: SocialAccountLite[] }>("/api/social-accounts")
      .then((res) => {
        const list = (res.social_accounts ?? []).filter(
          (a) => a.status === "active"
        );
        setAccounts(list);
        const first = list[0];
        if (first) setAccountId(first.id);
      })
      .catch(() => setAccounts([]));
  }, []);

  useEffect(() => {
    const acct = accounts.find((a) => a.id === accountId);
    if (!acct) {
      setBestSlots([]);
      return;
    }
    api
      .get<{ buckets: { utc_day: number; utc_hour: number; post_count: number; avg_score: number; best_score: number }[]; total_posts: number }>(
        `/api/analytics/optimal-times?platform=${acct.platform}&days=90`
      )
      .then((res) => {
        if (res.total_posts < 3) {
          setBestSlots([]);
          return;
        }
        const offsetMinutes = -new Date().getTimezoneOffset();
        const cells = new Map<string, { day: number; hour: number; post_count: number; avg_score: number }>();
        for (const b of res.buckets) {
          let totalMinutes = b.utc_day * 24 * 60 + b.utc_hour * 60 + offsetMinutes;
          const minutesInWeek = 7 * 24 * 60;
          totalMinutes = ((totalMinutes % minutesInWeek) + minutesInWeek) % minutesInWeek;
          const localDay = Math.floor(totalMinutes / (24 * 60));
          const localHour = Math.floor((totalMinutes % (24 * 60)) / 60);
          const key = `${localDay}-${localHour}`;
          const cur = cells.get(key) ?? { day: localDay, hour: localHour, post_count: 0, avg_score: 0 };
          cur.post_count += b.post_count;
          cur.avg_score = (cur.avg_score * (cur.post_count - b.post_count) + b.avg_score * b.post_count) / Math.max(cur.post_count, 1);
          cells.set(key, cur);
        }
        const top = Array.from(cells.values())
          .filter((c) => c.post_count > 0)
          .sort((a, b) => b.avg_score - a.avg_score)
          .slice(0, 3);
        setBestSlots(top);
      })
      .catch(() => setBestSlots([]));
  }, [accountId, accounts]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!accountId) return;
    const when_ms = new Date(when).getTime();
    if (Number.isNaN(when_ms)) {
      toast.error("Pick a valid date/time.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/scheduled-posts", {
        social_account_id: accountId,
        content: text,
        scheduled_for: when_ms,
        variant_id: variant.id,
        project_id: projectId,
        platform: variant.platform,
        media_asset_ids: mediaIds.length > 0 ? mediaIds : undefined,
        first_comment:
          variant.platform === "linkedin" && firstComment.trim()
            ? firstComment.trim()
            : undefined,
      });
      onScheduled();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md rounded-2xl border border-ink-200/60 bg-white p-5 shadow-xl animate-scale-in">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-ink-900">Schedule post</h3>
          <button
            onClick={onClose}
            className="text-ink-500 hover:text-ink-900"
            type="button"
          >
            ×
          </button>
        </div>
        {accounts.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-ink-500">
              No connected accounts yet. Connect one in Settings → Social
              accounts to schedule.
            </p>
            <Link to="/app/settings" className="btn-primary inline-flex no-underline">
              Open settings
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                Account
              </label>
              <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="input text-sm"
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.platform} · @{a.handle}
                    {a.display_name ? ` (${a.display_name})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                When
              </label>
              <input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="input text-sm"
              />
              <p className="mt-1 text-xs text-ink-500">
                Local time. Cron checks every minute.
              </p>
              {bestSlots.length > 0 ? (
                <div className="mt-2">
                  <p className="text-xs font-medium text-ink-600">
                    Best slots (from your data):
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {bestSlots.map((s, i) => (
                      <button
                        key={`${s.day}-${s.hour}`}
                        type="button"
                        onClick={() =>
                          setWhen(formatLocalDatetime(nextOccurrenceMs(s.day, s.hour)))
                        }
                        className="rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-[11px] text-brand-800 hover:bg-brand-100"
                        title={`avg engagement ${s.avg_score.toFixed(1)} · ${s.post_count} post(s)`}
                      >
                        #{i + 1} {DAY_SHORT[s.day]} {formatHourShort(s.hour)}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700">
                Images <span className="text-ink-400">(optional)</span>
              </label>
              <MediaPicker
                value={mediaIds}
                onChange={setMediaIds}
                max={4}
                disabled={submitting}
              />
            </div>
            {variant.platform === "linkedin" ? (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink-700">
                  Sticky first comment{" "}
                  <span className="text-ink-400">(optional)</span>
                </label>
                <textarea
                  className="input min-h-[80px] w-full text-sm"
                  value={firstComment}
                  onChange={(e) => setFirstComment(e.target.value)}
                  placeholder="Drop your link or CTA here — LinkedIn deboosts links in the main post"
                  maxLength={1250}
                  disabled={submitting}
                />
                <p className="mt-1 text-xs text-ink-500">
                  Posted automatically as the first comment after the post goes
                  live · {firstComment.length}/1250
                </p>
              </div>
            ) : null}
            <div className="rounded-lg bg-ink-50 p-3 text-xs text-ink-700">
              <p className="font-medium text-ink-900">Preview</p>
              <p className="mt-1 line-clamp-4 whitespace-pre-wrap">{text}</p>
              <p className="mt-2 text-ink-500">{text.length} chars</p>
            </div>
            <div className="flex justify-end gap-2 border-t border-ink-100 pt-3">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting || !accountId}
              >
                {submitting ? "Scheduling…" : "Schedule"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ModelOption({
  value,
  plan,
  children,
}: {
  value: VariantModel;
  plan: Plan;
  children: React.ReactNode;
}) {
  const allowed = isModelAllowed(value, plan);
  const required = MODEL_MIN_PLAN[value];
  const lockTag = allowed
    ? ""
    : `  ·  ${required.charAt(0).toUpperCase() + required.slice(1)}+`;
  return (
    <option value={value} disabled={!allowed}>
      {allowed ? children : `🔒 ${children}${lockTag}`}
    </option>
  );
}

function PillRadio({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors ${
        active
          ? "border-ink-900 bg-ink-900 text-white"
          : "border-ink-200 bg-white text-ink-700 hover:border-ink-400"
      }`}
    >
      {label}
    </button>
  );
}
